import React, { useEffect, useState } from 'react';
import { CreditCard, Trash2, ShieldCheck, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../App';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from '../ui/alert-dialog';

// Brand logo mapping, Stripe returns brand as lowercase string.
const BRAND_STYLES = {
  visa: { label: 'Visa', bg: '#1A1F71', color: '#fff' },
  mastercard: { label: 'Mastercard', bg: '#EB001B', color: '#fff' },
  amex: { label: 'Amex', bg: '#2E77BB', color: '#fff' },
  discover: { label: 'Discover', bg: '#FF6600', color: '#fff' },
  diners: { label: 'Diners', bg: '#0079BE', color: '#fff' },
  jcb: { label: 'JCB', bg: '#0E4C96', color: '#fff' },
  unionpay: { label: 'UnionPay', bg: '#E21836', color: '#fff' },
};

function formatExpiry(month, year) {
  if (!month || !year) return '';
  return `${String(month).padStart(2, '0')}/${String(year).slice(-2)}`;
}

function CardItem({ card, onRemove, removing }) {
  const brandKey = (card.brand || '').toLowerCase();
  const style = BRAND_STYLES[brandKey] || { label: card.brand?.toUpperCase() || 'Card', bg: '#1A1A1A', color: '#D4AF37' };
  const hasPendingFee = card.bookings.some(b => b.fee_pending);
  const hasActiveBookings = card.bookings.some(b => ['pending_payment', 'confirmed'].includes(b.status));

  return (
    <div
      className="card-surface p-5 flex flex-col sm:flex-row sm:items-center gap-4"
      data-testid={`card-item-${card.payment_method_id}`}
    >
      {/* Brand pill */}
      <div
        className="w-14 h-9 rounded flex items-center justify-center font-bold text-xs shrink-0"
        style={{ background: style.bg, color: style.color }}
      >
        {style.label}
      </div>

      {/* Card details */}
      <div className="flex-1 min-w-0">
        <p className="text-[#1A1A1A] font-mono text-sm tracking-wider">
          •••• •••• •••• {card.last4}
        </p>
        <p className="text-xs text-stone-400 mt-1">
          Expires {formatExpiry(card.exp_month, card.exp_year)}
          {card.bookings.length > 0 && (
            <> &middot; On file for {card.bookings.length} booking{card.bookings.length === 1 ? '' : 's'}</>
          )}
        </p>

        {hasPendingFee && (
          <div className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
            <AlertCircle className="h-3 w-3" />
            Fee pending capture
          </div>
        )}
      </div>

      {/* Remove */}
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={removing || hasActiveBookings}
            className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 hover:border-red-300 shrink-0 gap-1.5"
            data-testid={`remove-card-btn-${card.payment_method_id}`}
            title={hasActiveBookings ? 'Cancel upcoming bookings first' : 'Remove saved card'}
          >
            {removing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Remove
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove saved card?</AlertDialogTitle>
            <AlertDialogDescription>
              Your <strong>{style.label} •••• {card.last4}</strong> will be permanently detached. If you have any late-cancellation or no-show fees scheduled, those charges will fail, the provider may contact you directly to settle.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-card-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => onRemove(card.payment_method_id)}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="remove-card-confirm"
            >
              Remove card
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function PaymentMethodsSection() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);

  const load = () => {
    setLoading(true);
    api.get('/cards/me')
      .then(res => setCards(res.data.cards || []))
      .catch(() => setCards([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleRemove = async (pmId) => {
    setRemoving(pmId);
    try {
      await api.delete(`/cards/${pmId}`);
      toast.success('Card removed');
      setCards(prev => prev.filter(c => c.payment_method_id !== pmId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove card');
    } finally { setRemoving(null); }
  };

  if (loading) {
    return (
      <div className="text-sm text-stone-400 py-12 text-center">Loading your saved cards...</div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payment-methods-section">
      {/* Header / trust banner */}
      <div className="bg-gradient-to-br from-[#F9F6F0] to-white border border-[#E6E4DD] rounded-xl p-5 flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center shrink-0">
          <ShieldCheck className="h-5 w-5 text-[#D4AF37]" />
        </div>
        <div>
          <h3 className="font-semibold text-[#1A1A1A] mb-1">Your saved cards</h3>
          <p className="text-sm text-stone-500 leading-relaxed">
            Cards are stored securely with Stripe and never touch Zuriya's servers. They're only charged if a provider's cancellation policy is enforced (late cancel or no-show). You'll always be notified before any charge.
          </p>
        </div>
      </div>

      {cards.length === 0 ? (
        <div className="card-surface p-4 sm:p-12 text-center">
          <div className="w-14 h-14 rounded-full bg-stone-100 mx-auto mb-4 flex items-center justify-center">
            <CreditCard className="h-6 w-6 text-stone-400" />
          </div>
          <p className="text-[#1A1A1A] font-medium mb-1">No saved cards yet</p>
          <p className="text-sm text-stone-500 max-w-sm mx-auto">
            When a booking requires a card on file, you'll be prompted to add one, and it'll appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {cards.map(c => (
            <CardItem
              key={c.payment_method_id}
              card={c}
              removing={removing === c.payment_method_id}
              onRemove={handleRemove}
            />
          ))}
        </div>
      )}
    </div>
  );
}
